/**
 * ProjectBoardCard — one project as a draggable tile on the stage board.
 *
 * Deliberately small: on a board the card is scanned, not read. Title, status,
 * then the two facts that decide what to do next — when it is due and how much
 * is attached — plus who owns it. Everything else is one click away on the
 * project itself.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiCalendarBlankLight, PiLinkSimpleLight } from 'react-icons/pi';
import GlassCard from '../shared/brand/GlassCard';
import Badge from '../shared/TailwindComponents/Badge';
import ProfileImage from '../Header/ProfileImage';
import { formatDateTime } from '../../utils/functions';
import { dueTone as dueToneFor, statusColorMap, titleCase } from './projectFacts';

interface Props {
  project: any;
  onClick: (uuid: string) => void;
}

const initialsOf = (name: string): string =>
  name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('');

const ProjectBoardCard: React.FC<Props> = ({ project, onClick }) => {
  const { t } = useTranslation();
  const dueTone = dueToneFor(project.due_date);

  return (
    <GlassCard
      padding="sm"
      className="cursor-pointer transition-shadow hover:shadow-md"
    >
      <div onClick={() => onClick(project.uuid)}>
        <p className="text-sm font-medium text-fg-strong line-clamp-2">
          {project.title || t('Untitled')}
        </p>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <Badge color={statusColorMap[project.status] || 'gray'}>
            {project.status ? titleCase(project.status) : t('Active')}
          </Badge>
          {project.resource_count > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-fg-muted">
              <PiLinkSimpleLight className="h-3 w-3" />
              {project.resource_count}
            </span>
          )}
        </div>

        {(project.due_date || project.owner?.name) && (
          <div className="mt-2 flex items-center justify-between gap-2">
            {project.due_date ? (
              <span
                className={clsx(
                  'inline-flex items-center gap-1 text-[11px]',
                  dueTone === 'danger'
                    ? 'text-danger-600 dark:text-danger-400'
                    : dueTone === 'warning'
                      ? 'text-warning-600 dark:text-warning-400'
                      : 'text-fg-muted'
                )}
              >
                <PiCalendarBlankLight className="h-3 w-3" />
                {formatDateTime(project.due_date)}
              </span>
            ) : <span />}
            {project.owner?.name && (
              <ProfileImage
                profilePicture={project.owner.profile_picture || null}
                email={project.owner.email || null}
                initials={initialsOf(project.owner.name)}
                name={project.owner.name}
                size="w-5 h-5"
              />
            )}
          </div>
        )}
      </div>
    </GlassCard>
  );
};

export default ProjectBoardCard;
